import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap, withLatestFrom, distinctUntilChanged } from 'rxjs/operators';


// Services.
import { LoggerService } from '../logger/logger.service';

// Classes.
import { Logger } from '../../classes/logger';
import { StorageService } from '../storage/storage.service';
import { of } from 'rxjs';
import { LanguageService } from '../lang/language.service';
import { mergeMap } from 'rxjs/operators';
import { Interpretations } from '../../classes/interpretations';


@Injectable({
  providedIn: 'root'
})
export class FetchService {

  protected apiUrl = 'https://api.worldarchery.sport/v3/';

  protected logger: Logger;

  constructor(
    private http: HttpClient,
    loggerSrvc: LoggerService,
    private lang: LanguageService
  ) {
    this.logger = new Logger(loggerSrvc, this.constructor.name);
  }


  public getApiURL() {
    return this.apiUrl;
  }


  /**
   * Fetches an article from the Rulebook.
   * https://api.worldarchery.sport/v3/API/RB-Article
   */
  public getArticle(id: string): Observable<any> {
    let url = this.apiUrl + 'RB-ARTICLE/?ArticleID=' + id;

    return this.http.get(url).pipe(
      map(response => this.processArticleResponse(response)),
      tap(items => this.logger.log(`Received items from "${url}"`, items))
    )
  }

  /**
   * Process this.article http response.
   *
   * Always return first element.
   */
  private processArticleResponse(data: any): any {
    return data.items[0]
  }

  /**
   * Index.
   * Returns the main index of the rulebook from the given article id.
   * https://api.worldarchery.sport/v3/API/RB-Index
   */
  public getIndex(id: string): Observable<any> {
    let url = this.apiUrl + 'RB-INDEX/?ArticleID=' + id;

    this.logger.log(`Requesting index "${id}" to URL "${url}"`)
    return this.http.get(url).pipe(
      map(response => this.processIndexResponse(response, id),
        tap(items => this.logger.log(`Received index items from "${url}"`, items))
      ));
  }

  /**
   * Process this.index http response.
   *
   * If id requested is 0 we wrap results in a ghost item to always return
   * a single item. If not, we have tio return an array of items.
   */
  private processIndexResponse(data: any, id: string): any {
    let response: any;

    if (id == "0") {
      response = {
        kicker: "Rulebook",
        title: "Rulebook container article",
        number: 0,
        order: 0,
        id: "0",
        type: "txt",
        parent: null,
        level: 0,
        pdf: "",
        children: data.items
      };
    }
    else {
      response = data.items[0]
    }

    return response;
  }


  /**
   * Image.
   * Returns an image from Rulebook. The image is base64-encoded.
   * https://api.worldarchery.sport/v3/API/RB-Image
   */
  public getImage(id: number): Observable<any> {
    let url = this.apiUrl + 'RB-IMAGE/?ImageID=' + id;

    return this.http.get(url).pipe(
      map(response => this.processImageResponse(response)),
      map(rawImage => rawImage)
    )
  }

  private processImageResponse(data: any) {
    return data.items[0];
  }


  /**
   * Search.
   * Returns a list of articles where the text is found in kicker, title or text.
   */
  public search(text: string, page: number): Observable<any> {
    // https://api.worldarchery.sport/v3/API/RB-Search
    let url = this.apiUrl + 'RB-SEARCH/?fText=' + text;
    if (page) {
      url += '&Page=' + page;
    }

    return this.http.get(url).pipe(
      map(response => this.processSearchResponse(response))
    );
  }

  private processSearchResponse(data: any) {
    return data;
  }



  /**
   * Flying page.
   *
   * Returns a flying page.
   */
  public getFlyingPage(id: string): Observable<any> {
    // https://api.worldarchery.sport/v3/API/RB-Search
    let url = this.apiUrl + 'RB-PAGE/?RBPage=' + id;

    return this.http.get(url).pipe(
      map(response => this.processFlyingPageResponse(response))
    );
  }

  private processFlyingPageResponse(data: any) {
    return data.items[0];
  }

  /**
   *
   *  Get interpretations from api call
   * @returns
   * @memberof FetchService
   */
  public getInterpretations() {
    const url = this.apiUrl + 'RB-Interpretation/';
    return this.http.get(url).pipe(
      map((response: { items: Array<Interpretations> }) => response.items.map(data => new Interpretations(data)))
    )
  }
}
